// The OCS Action Danger Scale, draft v0.1 (docs/13 "Free tools"): six levels, what belongs in each,
// and the default policy an agent broker applies. A draft for the community to argue with.

export interface DangerLevel {
  level: number;
  name: string;
  summary: string;
  examples: string[];
  policy: string;
  /** Tailwind classes for the level's colour. */
  tone: string;
}

export const DANGER_SCALE: DangerLevel[] = [
  {
    level: 0,
    name: "Observe",
    summary: "Reads what anyone could read.",
    examples: ["search the web", "read public documentation", "look up a public package"],
    policy: "Allow. Log.",
    tone: "bg-moss/15 text-moss border-moss/40",
  },
  {
    level: 1,
    name: "Read private",
    summary: "Reads what belongs to you or your organisation.",
    examples: ["read your email or calendar", "read files, tickets and code", "query a database read-only"],
    policy: "Allow within the task's scope. Log every read.",
    tone: "bg-teal/15 text-teal border-teal/40",
  },
  {
    level: 2,
    name: "Prepare",
    summary: "Makes something no one else sees yet.",
    examples: ["write a draft", "create a branch", "stage a change for review"],
    policy: "Allow. Log.",
    tone: "bg-sky/15 text-sky border-sky/40",
  },
  {
    level: 3,
    name: "Act as you, reversibly",
    summary: "Does something others see, that can be undone.",
    examples: ["send a calendar invite", "comment on a ticket", "open a pull request", "label an issue"],
    policy: "Allow by policy. Tell the owner.",
    tone: "bg-mustard/15 text-mustard border-mustard/50",
  },
  {
    level: 4,
    name: "Act as you, for good",
    summary: "Does something outward or irreversible.",
    examples: ["email people outside the company", "merge to main", "publish", "pay", "delete data"],
    policy: "A person approves each one, on their own device.",
    tone: "bg-signal/10 text-signal border-signal/40",
  },
  {
    level: 5,
    name: "Change the rules",
    summary: "Changes who can do what.",
    examples: ["grant permissions", "create keys or tokens", "change security settings", "add users"],
    policy: "Never delegated to an agent.",
    tone: "bg-ink text-paper border-ink",
  },
];

/** Common agent actions, classed (the table the scale page searches). */
export const CLASSED_ACTIONS: { action: string; tool: string; level: number }[] = [
  { tool: "web", action: "search", level: 0 },
  { tool: "docs", action: "read public page", level: 0 },
  { tool: "email", action: "read inbox", level: 1 },
  { tool: "calendar", action: "read events", level: 1 },
  { tool: "jira", action: "get issue", level: 1 },
  { tool: "github", action: "read repository", level: 1 },
  { tool: "database", action: "select (read-only)", level: 1 },
  { tool: "email", action: "save a draft", level: 2 },
  { tool: "git", action: "create a branch", level: 2 },
  { tool: "docs", action: "write a draft document", level: 2 },
  { tool: "calendar", action: "create an invite", level: 3 },
  { tool: "jira", action: "comment", level: 3 },
  { tool: "jira", action: "add a label", level: 3 },
  { tool: "github", action: "open a pull request", level: 3 },
  { tool: "slack", action: "post in a team channel", level: 3 },
  { tool: "email", action: "send outside the company", level: 4 },
  { tool: "git", action: "push to main", level: 4 },
  { tool: "github", action: "merge a pull request", level: 4 },
  { tool: "payments", action: "pay or refund", level: 4 },
  { tool: "database", action: "delete rows", level: 4 },
  { tool: "cms", action: "publish", level: 4 },
  { tool: "iam", action: "grant a role", level: 5 },
  { tool: "github", action: "create a token", level: 5 },
  { tool: "workspace", action: "add a user", level: 5 },
  { tool: "security", action: "turn off two-factor", level: 5 },
];
